import Vue from 'vue';
import {
	JustAddedBucket,
	JustAddedEntry,
	forgetJustAdded,
	forgetJustAddedRow,
	justAddedBucket,
	mergeJustAddedRows,
	stampJustAdded,
} from '@/utils/justAdded';

/**
 * A Postgres-backed table that shows rows just added (utils/justAdded.ts) before
 * its listing can: pinned on top of its first page, marked as just added, and
 * never twice, since the listing's own copy is hidden while one is pinned.
 *
 * The host supplies:
 *  - `justAddedListKey` (computed): which list it is (JUST_ADDED_LIST), or null
 *    until it knows;
 *  - `rows` (data), `loading` (data), `searchQuery` (data), `canWalkBack`
 *    (computed), `appliedPanelFilters` (computed, FiltersPanelMixin) and
 *    `refetch()` (method), which every such table has;
 *  - `justAddedRowId(row)` (method, optional): a row's id, when it is not `row.id`.
 *
 * It gives the table `listedRows` to show, `isJustAdded(row)` to mark a row,
 * `refreshList()` for its Refresh button and `forgetJustAddedRow(id)` for a row
 * whose document is gone. A refresh lets go of the pinned rows, and so does a
 * new search or filter: the listing then stands alone.
 *
 * The search and filters a table restores as it opens are not a change: it only
 * listens once its first page has arrived, and pinned rows belong to the search
 * and filters it first showed them under.
 */
export const JustAddedRowsMixin = Vue.extend({
	data () {
		return {
			// The table's first page has arrived, so its search and filters are its own
			justAddedSettled: false,
		};
	},
	computed: {
		// What the list is narrowed by
		justAddedFiltersKey (): string {
			return JSON.stringify([this.searchQuery || '', this.appliedPanelFilters || null]);
		},
		justAddedListBucket (): JustAddedBucket | null {
			return justAddedBucket(this.justAddedListKey);
		},
		justAddedRows (): unknown[] {
			const bucket: JustAddedBucket | null = this.justAddedListBucket;
			if (!bucket || !bucket.entries.length) return [];
			if (bucket.filtersKey !== null && bucket.filtersKey !== this.justAddedFiltersKey) return [];
			// The top of the list is its first page; the pages after it are the listing's alone
			if (this.canWalkBack) return [];
			return bucket.entries.map((entry: JustAddedEntry) => entry.row);
		},
		justAddedIds (): Record<string, true> {
			const ids: Record<string, true> = {};
			this.justAddedRows.forEach((row: unknown) => {
				ids[this.justAddedRowId(row)] = true;
			});
			return ids;
		},
		// The rows to show: the pinned ones, then the listing's without its copies of them
		listedRows (): unknown[] {
			return mergeJustAddedRows(this.justAddedRows, this.rows, (row: unknown) => this.justAddedRowId(row));
		},
		justAddedNeedsStamp (): boolean {
			const bucket: JustAddedBucket | null = this.justAddedListBucket;
			return !!bucket && bucket.filtersKey === null;
		},
	},
	watch: {
		loading (isLoading: boolean, wasLoading: boolean) {
			if (this.justAddedSettled || isLoading || !wasLoading) return;
			this.justAddedSettled = true;
			stampJustAdded(this.justAddedListKey, this.justAddedFiltersKey);
		},
		// Rows noted while the table is open take its search and filters as theirs
		justAddedNeedsStamp (needs: boolean) {
			if (needs && this.justAddedSettled) stampJustAdded(this.justAddedListKey, this.justAddedFiltersKey);
		},
		// A new search or filter: the listing stands alone
		justAddedFiltersKey (newKey: string, oldKey: string) {
			if (!this.justAddedSettled || newKey === oldKey) return;
			const bucket: JustAddedBucket | null = this.justAddedListBucket;
			if (bucket && bucket.filtersKey !== null && bucket.filtersKey !== newKey) {
				forgetJustAdded(this.justAddedListKey);
			}
		},
	},
	methods: {
		justAddedRowId (row: unknown): string {
			return (row as { id: string }).id;
		},
		isJustAdded (row: unknown): boolean {
			return !!this.justAddedIds[this.justAddedRowId(row)];
		},
		// The Refresh button: the listing alone, fetched again
		refreshList (): Promise<void> {
			forgetJustAdded(this.justAddedListKey);
			return this.refetch();
		},
		// A pinned row's document is gone: it is not pinned any more
		forgetJustAddedRow (id: string) {
			forgetJustAddedRow(this.justAddedListKey, id);
		},
	},
});
